import { useEffect, useRef, useState } from "react";
import { useWebSocket } from "../lib/ws-context";
import {
  loadStudentSession,
  saveStudentSession,
} from "../lib/session-storage";

const MAX_REJOINS_PER_WINDOW = 3;
const REJOIN_WINDOW_MS = 30_000;

interface RejoinState {
  sessionCode: string;
  displayName: string;
}

interface RejoinResult {
  /**
   * True when the hook has given up auto-rejoining because it fired too
   * many times in a short window. That pattern means another tab of the
   * same browser holds this student's resume token and keeps evicting us
   * (the one-generation token grace makes the eviction mutual, so two
   * live tabs can ping-pong the connection indefinitely — each cycle
   * costing DDB writes and a class-wide broadcast). Damping here breaks
   * the loop; screens surface "connected in another tab".
   */
  superseded: boolean;
  /**
   * The name the *server* currently knows this student by. Starts from the
   * saved session (or the route state) and is updated whenever a
   * `SESSION_JOINED` lands — the server can hand back a *different* name
   * than we asked for (a name collision in free mode, or a fresh character
   * when the old one was still held). Every identity comparison on the
   * `/play/*` screens (leaderboard `mine` lookups, highlight, the name
   * forwarded in route state) must use this, not the route state's
   * `displayName`, or a renamed student sees score 0 / "Wrong!" / no
   * highlight for the rest of the quiz.
   */
  displayName: string;
}

// Re-JOIN the session whenever the socket is not open and we know which
// session we belong to. Covers:
//   - page reload (ws status resets to "idle", router state survives)
//   - a socket dropped mid-question (phone lock, app switch) — previously
//     PlayQuestion had no rejoin, so the next tap opened a fresh socket
//     and SUBMIT_ANSWER went out un-joined ("Not joined to a session").
// The guard resets every time the socket reaches "open", so a second
// drop on the same screen triggers a second rejoin.
//
// "connecting" is deliberately NOT skipped. `ws.send()` auto-connects, so
// a tap on a dead socket flips the status straight from "open" to
// "connecting" (the old socket's `close` event lands after `connect()`
// has already replaced `wsRef`, so it never sets "closed"). Skipping
// "connecting" would leave that queue holding a lone SUBMIT_ANSWER and
// the server would answer "Not joined to a session" — the exact incident
// this hook exists to fix. `rejoinedRef` is what prevents a double JOIN;
// the status check is not needed for that.
//
// The rejoin carries the saved `playerToken`. That's the real fix for the
// phone-lock case: a half-open socket can look alive to API Gateway for
// ~10 minutes, and without a token the server refuses to hand the PLAYER
// row back to us. A valid token takes the row over regardless.
//
// This hook also owns the inbound `SESSION_JOINED` for the `/play/*`
// screens — none of them handled it before, so a server-side rename went
// unnoticed and localStorage kept re-requesting the stale name forever.
export function useStudentRejoin(
  state: RejoinState | null | undefined,
): RejoinResult {
  const ws = useWebSocket();
  const rejoinedRef = useRef(false);
  // Timestamps of recent auto-rejoins, for the ping-pong damper.
  const rejoinTimesRef = useRef<number[]>([]);
  const [superseded, setSuperseded] = useState(false);
  const [displayName, setDisplayName] = useState<string>(() => {
    const saved = loadStudentSession();
    if (state && saved && saved.sessionCode === state.sessionCode) {
      return saved.displayName;
    }
    return state?.displayName ?? "";
  });

  // Depend on the primitives, not on `state`/`ws`. Callers pass the router's
  // `location.state`, and `ws` is a memo that changes on every inbound frame —
  // with either in the deps this effect re-ran on every message and only
  // `rejoinedRef` kept it from misbehaving. `ws.send` is a stable useCallback.
  const sessionCode = state?.sessionCode;
  const routeName = state?.displayName;
  const wsStatus = ws.status;
  const wsSend = ws.send;

  useEffect(() => {
    if (wsStatus === "open") {
      rejoinedRef.current = false;
      return;
    }
    // Both are undefined exactly when the caller passed no state at all.
    if (sessionCode === undefined || routeName === undefined) return;
    if (rejoinedRef.current || superseded) return;
    // Damping: >MAX_REJOINS auto-rejoins inside WINDOW_MS is the two-tab
    // eviction ping-pong, not a flaky network. Stop feeding the loop.
    const now = Date.now();
    const recent = rejoinTimesRef.current.filter(
      (t) => now - t < REJOIN_WINDOW_MS,
    );
    if (recent.length >= MAX_REJOINS_PER_WINDOW) {
      rejoinTimesRef.current = recent;
      setSuperseded(true);
      return;
    }
    recent.push(now);
    rejoinTimesRef.current = recent;
    rejoinedRef.current = true;
    const loaded = loadStudentSession();
    const saved = loaded && loaded.sessionCode === sessionCode ? loaded : null;
    wsSend({
      type: "JOIN_SESSION",
      sessionCode,
      displayName: saved?.displayName ?? routeName,
      ...(saved?.playerToken ? { playerToken: saved.playerToken } : {}),
    });
  }, [sessionCode, routeName, wsStatus, wsSend, superseded]);

  // Absorb the join ack. We deliberately do NOT `ws.consume()` here: no
  // `/play/*` screen reads `SESSION_JOINED` (verified — only Join.tsx and
  // HostLobby.tsx do), so leaving it in place costs nothing, while
  // consuming it from a shared hook would silently swallow the frame if a
  // future screen ever wants it.
  useEffect(() => {
    const msg = ws.lastMessage;
    if (!msg || msg.type !== "SESSION_JOINED") return;
    // A join ack means we're back in — clear the two-tab damper (the other
    // tab was closed, or the teacher restarted things).
    setSuperseded(false);
    setDisplayName(msg.displayName);
    const prev = loadStudentSession();
    saveStudentSession({
      ...prev,
      sessionCode: msg.sessionCode,
      displayName: msg.displayName,
      playerToken: msg.playerToken ?? prev?.playerToken,
      nameMode: msg.nameMode,
      languageMode: msg.languageMode ?? prev?.languageMode,
      availableLanguages: msg.availableLanguages ?? prev?.availableLanguages,
    });
  }, [ws.lastMessage]);

  return { displayName: displayName || (routeName ?? ""), superseded };
}
